import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import TiptapViewer from "../../../components/layout/tiptapViewer/TiptapViewer";
import { IconButton } from "../../../components/ui";
import SendEmailModal from "../../../components/ui/SendEmailModal";
import {
  buildUsiFormDefaults,
  getUsiConclusionBlock,
  getUsiFormSchema,
  getUsiResultBlocks,
  getUsiTypeParam,
} from "./usiFormSchema";
import { usiText } from "./usiLabels";
import "../../../components/layout/tiptapEditor/tiptapEditor.css";
import "./PatientPlanPrint.css";
import "./PatientPlanPrintOtherFormat.css";
import "./PatientUziPrint.css";

const parseDocData = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
};

const formatDate = (value) => {
  const raw = String(value ?? "").trim();
  if (
    !raw ||
    raw === "—" ||
    raw === "0000-00-00" ||
    raw.startsWith("0000-00-00")
  ) {
    return "";
  }
  const isoDate = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return raw;
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
};

const displayValue = (value) => {
  if (value === 0) return "0";
  return String(value ?? "").trim();
};

const PrintField = ({ label, value, suffix, full = false }) => (
  <div
    className={
      full
        ? "plan-print-other-field plan-print-other-field--full"
        : "plan-print-other-field"
    }>
    <span className="plan-print-other-field-label">{label}:</span>
    <span className="plan-print-other-field-value">{value || "\u00A0"}</span>
    {suffix ? (
      <span className="plan-print-other-field-suffix">{suffix}</span>
    ) : null}
  </div>
);

const GoldDivider = () => (
  <div className="plan-print-other-header-divider" aria-hidden="true">
    <span className="plan-print-other-header-divider-line" />
    <span className="plan-print-other-header-divider-diamond-wrap">
      <span className="plan-print-other-header-divider-diamond-outline" />
      <span className="plan-print-other-header-divider-diamond" />
    </span>
    <span className="plan-print-other-header-divider-line" />
  </div>
);

const PlanPrintOtherLogoIcon = () => (
  <svg
    className="plan-print-other-logo-icon"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M12 2.4C12 2.4 4.6 10.1 4.6 15.2a7.4 7.4 0 0 0 14.8 0C19.4 10.1 12 2.4 12 2.4z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12 5.8C12 5.8 7.2 11.2 7.2 14.9a4.8 4.8 0 0 0 9.6 0C16.8 11.2 12 5.8 12 5.8z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12 9.2C12 9.2 9.4 12.4 9.4 14.7a2.6 2.6 0 0 0 5.2 0C14.6 12.4 12 9.2 12 9.2z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const UltrasoundIcon = () => (
  <svg
    className="plan-print-other-section-rail-icon"
    viewBox="0 0 24 24"
    fill="none">
    <rect
      x="4.2"
      y="3.4"
      width="15.6"
      height="11.4"
      rx="1.6"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M7.2 7.4c1.6 2.2 3.2 2.2 4.8 0s3.2-2.2 4.8 0"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M9.4 14.8v2.4H7.2v1.8h9.6v-1.8h-2.2v-2.4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const ClipboardCheckIcon = () => (
  <svg
    className="plan-print-other-section-rail-icon"
    viewBox="0 0 24 24"
    fill="none">
    <rect
      x="6"
      y="5.2"
      width="12"
      height="14.6"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M9.2 4.4h5.6v2.4H9.2V4.4z"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M9.2 12.2l1.8 1.8 3.8-4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TargetIcon = () => (
  <svg
    className="plan-print-other-section-rail-icon"
    viewBox="0 0 24 24"
    fill="none">
    <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="12" r="4.6" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    <path
      d="M12 3.8V6.4M12 17.6v2.6M3.8 12H6.4M17.6 12h2.6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    className="plan-print-other-section-rail-icon"
    viewBox="0 0 24 24"
    fill="none">
    <rect
      x="4.2"
      y="5.4"
      width="15.6"
      height="14.2"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M4.2 9.4h15.6M8.2 3.8v3.2M15.8 3.8v3.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M8.2 13.2h.1M12 13.2h.1M15.8 13.2h.1M8.2 16.6h.1M12 16.6h.1"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);

const DoctorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="7.2" r="3.1" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M5.4 19.4c1-3.4 3.2-5.1 6.6-5.1s5.6 1.7 6.6 5.1"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M16.6 12.2v2.4a1.8 1.8 0 0 0 3.6 0v-1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle
      cx="20.2"
      cy="15.6"
      r="1.1"
      stroke="currentColor"
      strokeWidth="1.4"
    />
  </svg>
);

const InfoClipboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="6"
      y="5.2"
      width="12"
      height="14.6"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M9.2 4.4h5.6v2.4H9.2V4.4z"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <circle cx="12" cy="12.2" r="0.9" fill="currentColor" />
    <path
      d="M12 14.2v3.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PrintRail = ({ number, icon }) => (
  <div className="plan-print-other-section-rail" aria-hidden="true">
    {icon}
    <div className="plan-print-other-section-rail-number">{number}</div>
  </div>
);

const LinedBlock = ({ lines = 4, placeholder }) => (
  <div className="uzi-print-lined">
    {placeholder ? (
      <div className="uzi-print-placeholder">{placeholder}</div>
    ) : null}
    {Array.from({ length: lines }).map((_, index) => (
      <div
        className="plan-print-other-section-line"
        key={index}
        aria-hidden="true"
      />
    ))}
  </div>
);

const PrintTextBlock = ({ text, lines = 4 }) => {
  const value = displayValue(text);
  if (!value) return <LinedBlock lines={lines} />;
  return <div className="uzi-print-text">{value}</div>;
};

const looksLikeHtml = (value) =>
  /<\/?[a-z][a-z0-9]*\b[^>]*>/i.test(String(value ?? ""));

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const toRichHtml = (value) => {
  const raw = displayValue(value);
  if (!raw) return "";
  if (looksLikeHtml(raw)) return raw;
  return escapeHtml(raw).replace(/\r?\n/g, "<br />");
};

const PrintRichTextBlock = ({ text, lines = 4 }) => {
  const html = toRichHtml(text);
  if (!html) return <LinedBlock lines={lines} />;
  return (
    <TiptapViewer
      content={html}
      className="uzi-print-text uzi-print-rich-text"
    />
  );
};

const PrintFormItem = ({ item, value, t }) => {
  const text =
    item?.inputType === "date" ? formatDate(value) : displayValue(value);
  if (!text) return null;
  const label = usiText(item, "label", t);
  if (item.hideLabel || !label) {
    return <div className="uzi-print-text">{text}</div>;
  }
  return (
    <div className="uzi-print-item">
      <span className="uzi-print-item-label">{label}:</span>
      <span className="uzi-print-item-value">{text}</span>
    </div>
  );
};

const PrintDataTable = ({ block, formData, t }) => {
  const columns = block.columns || [];
  return (
    <div className="uzi-print-table-wrap">
      <table className="uzi-print-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{usiText(col, "label", t)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(block.rows || []).map((row) => (
            <tr key={row.id}>
              {columns.map((col) => (
                <td key={col.key}>
                  {col.readOnly
                    ? usiText(row, col.key, t)
                    : displayValue(formData[`${row.id}_${col.key}`])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PrintSpectralTable = ({ block, formData, t }) => (
  <div className="uzi-print-table-wrap">
    <table className="uzi-print-table">
      <thead>
        <tr>
          <th>{t("usi.lbl.arteriya")}</th>
          <th>{t("usi.lbl.storona")}</th>
          <th>{t("usi.lbl.psv_sm_s")}</th>
          <th>{t("usi.lbl.edv_sm_s")}</th>
          <th>{usiText(block, "spectrumLabel", t) || t("usi.lbl.spektr")}</th>
        </tr>
      </thead>
      <tbody>
        {(block.rows || []).map((row) => (
          <tr key={row.id}>
            <td>{usiText(row, "artery", t)}</td>
            <td>{usiText(row, "side", t)}</td>
            <td>{displayValue(formData[row.psv])}</td>
            <td>{displayValue(formData[row.edv])}</td>
            <td>{displayValue(formData[row.spectrum])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PrintSchemaBlock = ({ block, formData, t }) => {
  const title = usiText(block, "title", t);
  if (block.type === "spectral_table") {
    return (
      <div className="uzi-print-block">
        {title ? <div className="uzi-print-block-title">{title}</div> : null}
        <PrintSpectralTable block={block} formData={formData} t={t} />
      </div>
    );
  }

  if (block.type === "table") {
    return (
      <div className="uzi-print-block">
        {title ? <div className="uzi-print-block-title">{title}</div> : null}
        <PrintDataTable block={block} formData={formData} t={t} />
      </div>
    );
  }

  const filledItems = (block.items || []).filter((item) =>
    displayValue(formData[item.name]),
  );

  if (!filledItems.length) return null;

  return (
    <div className="uzi-print-block">
      {title ? <div className="uzi-print-block-title">{title}</div> : null}
      {filledItems.map((item) => (
        <PrintFormItem
          key={item.name}
          item={item}
          value={formData[item.name]}
          t={t}
        />
      ))}
    </div>
  );
};

const PrintResultsContent = ({ schema, formData, t }) => {
  const headerItems = schema?.headerItems || [];
  const resultBlocks = getUsiResultBlocks(schema);
  const hasHeader = headerItems.some((item) =>
    displayValue(formData[item.name]),
  );
  const hasBlocks = resultBlocks.length > 0;

  if (!hasHeader && !hasBlocks) {
    return <LinedBlock lines={8} />;
  }

  return (
    <div className="uzi-print-results">
      {headerItems.map((item) => (
        <PrintFormItem
          key={item.name}
          item={item}
          value={formData[item.name]}
          t={t}
        />
      ))}
      {resultBlocks.map((block, index) => (
        <PrintSchemaBlock
          key={block.title || `result-${index}`}
          block={block}
          formData={formData}
          t={t}
        />
      ))}
    </div>
  );
};

const PrintConclusionContent = ({ schema, formData, t }) => {
  const block = getUsiConclusionBlock(schema);
  const items = block?.items || [];
  const filledItems = items.filter((item) => displayValue(formData[item.name]));

  if (!filledItems.length) {
    return <PrintTextBlock text={formData.conclusion} lines={5} />;
  }

  return (
    <div className="uzi-print-conclusion">
      {filledItems.map((item) => (
        <PrintFormItem
          key={item.name}
          item={item}
          value={formData[item.name]}
          t={t}
        />
      ))}
    </div>
  );
};

function PatientUziPrint() {
  const { t } = useTranslation("patient");
  const { patientId, diagnosticsId } = useParams();
  const [loading, setLoading] = useState(Boolean(patientId && diagnosticsId));
  const [error, setError] = useState("");
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [patientFio, setPatientFio] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [examNumber, setExamNumber] = useState("");
  const [studyType, setStudyType] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadProtocol = async () => {
      if (!patientId || !diagnosticsId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const [viewRes, patientInfo] = await Promise.all([
          GetData({
            module: "patient",
            block: "diagnostics",
            action: "view",
            id_patient: patientId,
            id: diagnosticsId,
          }),
          GetData({
            module: "patient",
            block: "info",
            id_patient: patientId,
          }),
        ]);

        if (cancelled) return;

        if (viewRes instanceof Error || viewRes?.status?.success === false) {
          throw new Error(
            viewRes?.status?.message ||
              viewRes?.message ||
              t("usi.loadFailedShort"),
          );
        }

        const row =
          viewRes?.result && !Array.isArray(viewRes.result)
            ? viewRes.result
            : Array.isArray(viewRes?.result)
              ? viewRes.result[0]
              : null;

        if (!row?.id) {
          throw new Error(t("usi.notFound"));
        }

        const content = parseDocData(row.content);
        const typeName =
          getUsiTypeParam(content.usi_type || row.name) || row.name || "";
        const nextSchema = getUsiFormSchema(typeName);
        const defaults = buildUsiFormDefaults(nextSchema);
        const mapped = { ...defaults, ...content };

        if (row.date && !mapped.study_date) {
          mapped.study_date = String(row.date).slice(0, 10);
        }

        const patient = patientInfo?.result || patientInfo || {};

        if (!String(mapped.doctor || "").trim()) {
          const createdUserId = String(row.created_user_id ?? "").trim();
          const doctors = Array.isArray(patient?.all_doctors)
            ? patient.all_doctors
            : [];
          const creator = doctors.find(
            (item) =>
              String(item?.id ?? item?.id?.value ?? "") === createdUserId,
          );
          const creatorName = String(
            creator?.name ?? creator?.name?.value ?? "",
          ).trim();
          if (creatorName) {
            mapped.doctor = creatorName;
            mapped.doctor_id = createdUserId;
          }
        }

        setSchema(nextSchema);
        setFormData(mapped);
        setPatientFio(patient?.fio?.value || "");
        setBirthDate(formatDate(patient?.birthDate?.value || ""));
        setExamNumber(String(row.id || diagnosticsId));
        setStudyType(
          usiText(nextSchema, "printTitle", t) ||
            usiText(nextSchema, "heading", t) ||
            usiText(nextSchema, "title", t) ||
            row.name ||
            "",
        );
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t("usi.loadFailed"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProtocol();
    return () => {
      cancelled = true;
    };
  }, [patientId, diagnosticsId, t]);

  const doctorName = useMemo(() => {
    const raw = displayValue(formData.doctor);
    const comma = raw.indexOf(",");
    return comma === -1 ? raw : raw.slice(0, comma).trim();
  }, [formData.doctor]);
  const studyDate = formatDate(formData.study_date);
  const apparatus = displayValue(
    formData.device || formData.apparatus || formData.apparat,
  );

  if (loading) return <div className="plan-print-loading">{t("plan.loading")}</div>;
  if (error)
    return (
      <div className="plan-print-error">
        {t("usi.errorPrefix", { message: error })}
      </div>
    );

  return (
    <div className="patient-plan-print-page patient-plan-print-other patient-uzi-print">
      <div className="plan-print-controls no-print">
        <IconButton
          icon="print"
          onClick={() => window.print()}
          title={t("plan.buttons.print")}
          variant="secondary"
        />
        <IconButton
          icon="send"
          onClick={() => setEmailOpen(true)}
          title={t("docs.buttons.email")}
          variant="secondary"
        />
        <IconButton
          icon="back"
          onClick={() => window.history.back()}
          title={t("usi.backToList")}
          variant="primary"
        />
      </div>

      <header className="plan-print-other-header">
        <div className="plan-print-other-header-row">
          <div className="plan-print-other-header-brand">
            <PlanPrintOtherLogoIcon />
            <div className="plan-print-other-brand-text">
              <div className="plan-print-other-brand-name">REFRESH</div>
              <div className="plan-print-other-brand-sub">LONGEVITY CLINIC</div>
            </div>
          </div>
          <div className="plan-print-other-header-title-wrap">
            <div className="plan-print-other-header-title">
              {t("usi.protocol")}
              <br />
              {t("usi.protocolSubtitle")}
            </div>
            <GoldDivider />
          </div>
        </div>

        <div className="plan-print-other-patient-meta">
          <div className="plan-print-other-patient-meta-row">
            <PrintField label={t("usi.patientFio")} value={patientFio} full />
          </div>
          <div className="plan-print-other-patient-meta-row plan-print-other-patient-meta-row--triple">
            <PrintField label={t("usi.birthDate")} value={birthDate} />
            <PrintField label={t("usi.studyDate")} value={studyDate} />
            <PrintField label={t("usi.examNumber")} value={examNumber} />
          </div>
          <div className="plan-print-other-patient-meta-row">
            <PrintField label={t("usi.studyType")} value={studyType} full />
          </div>
          <div className="plan-print-other-patient-meta-row plan-print-other-patient-meta-row--split">
            <PrintField label={t("usi.apparatus")} value={apparatus} />
            <PrintField label={t("usi.doctor")} value={doctorName} />
          </div>
        </div>
      </header>

      <section className="plan-print-other-section uzi-print-section uzi-print-section--results">
        <PrintRail number="1" icon={<UltrasoundIcon />} />
        <div className="plan-print-other-section-body">
          <div className="plan-print-other-diagnostics-heading">
            {t("usi.sectionResults")}
          </div>
          <PrintResultsContent schema={schema} formData={formData} t={t} />
        </div>
      </section>

      <section className="plan-print-other-section uzi-print-section">
        <PrintRail number="2" icon={<ClipboardCheckIcon />} />
        <div className="plan-print-other-section-body">
          <div className="plan-print-other-diagnostics-heading">
            {t("usi.sectionConclusion")}
          </div>
          <PrintConclusionContent schema={schema} formData={formData} t={t} />
        </div>
      </section>

      <section className="plan-print-other-section uzi-print-section">
        <PrintRail number="3" icon={<TargetIcon />} />
        <div className="plan-print-other-section-body">
          <div className="plan-print-other-diagnostics-heading">
            {t("usi.sectionRecommendations")}
          </div>
          <PrintRichTextBlock text={formData.recommendations} lines={4} />
        </div>
      </section>

      <section className="plan-print-other-section uzi-print-section">
        <PrintRail number="4" icon={<CalendarIcon />} />
        <div className="plan-print-other-section-body">
          <div className="plan-print-other-diagnostics-heading">
            {t("usi.sectionFollowup")}
          </div>
          <div className="uzi-print-followup-term">
            <span className="plan-print-other-field-label">
              {t("usi.followupTerm")}
            </span>
            <span className="plan-print-other-field-value uzi-print-term-gap">
              {displayValue(formData.followup_term) || "\u00A0"}
            </span>
          </div>
          <PrintField
            label={t("usi.followupPurpose")}
            value={displayValue(formData.followup_purpose)}
            full
          />
          <div className="plan-print-other-section-line" aria-hidden="true" />
        </div>
      </section>

      <div className="uzi-print-footer">
        <div className="uzi-print-doctor">
          <span className="uzi-print-doctor-icon" aria-hidden="true">
            <DoctorIcon />
          </span>
          <div className="uzi-print-doctor-content">
            <div className="uzi-print-doctor-title">
              {t("usi.doctorTitle")}
            </div>
            <div className="uzi-print-doctor-signature">
              <div className="plan-print-other-signature-field plan-print-other-signature-field--fio">
                <span className="plan-print-other-signature-line">
                  {doctorName || "\u00A0"}
                </span>
                <span className="plan-print-other-signature-hint">
                  {t("usi.signatureFio")}
                </span>
              </div>
              <span className="plan-print-other-signature-sep">/</span>
              <div className="plan-print-other-signature-field plan-print-other-signature-field--sign">
                <span className="plan-print-other-signature-line">&nbsp;</span>
                <span className="plan-print-other-signature-hint">
                  {t("usi.signatureSign")}
                </span>
              </div>
            </div>
            <div className="plan-print-other-signature-date">
              <span className="plan-print-other-signature-label">
                {t("usi.dateLabel")}
              </span>
              <span className="plan-print-other-signature-line plan-print-other-signature-line--date">
                {studyDate || "\u00A0"}
              </span>
            </div>
          </div>
        </div>

        <div className="uzi-print-info">
          <span className="uzi-print-info-icon" aria-hidden="true">
            <InfoClipboardIcon />
          </span>
          <div className="uzi-print-info-content">
            <div className="uzi-print-info-title">{t("usi.infoTitle")}</div>
            <div className="uzi-print-info-text">{t("usi.infoText")}</div>
          </div>
        </div>
      </div>

      <div
        className="plan-print-other-header-divider uzi-print-slogan"
        aria-hidden="true">
        <span className="plan-print-other-header-divider-line" />
        <span className="plan-print-other-header-divider-diamond" />
        <span className="plan-print-other-diagnostics-heading">
          {t("usi.slogan")}
        </span>
        <span className="plan-print-other-header-divider-diamond" />
        <span className="plan-print-other-header-divider-line" />
      </div>

      {/* Отправка этой же печатной формы пациенту. PDF собирается из
          отрисованного листа (шапка, таблицы, цвета), а не из экранной формы. */}
      <SendEmailModal
        show={emailOpen}
        onClose={() => setEmailOpen(false)}
        idPatient={patientId}
        itemType="uzi"
        itemId={Number(diagnosticsId) || 0}
        documentTitle={`${studyType || t("usi.protocol")} — ${patientFio || ""}`.trim()}
        subject={`${studyType || t("usi.protocol")} — МИС РЕФРЕШ`}
        getRoot={() => document.querySelector(".patient-uzi-print")}
        onSent={() => setEmailOpen(false)}
      />
    </div>
  );
}

export default PatientUziPrint;
